import { useEffect, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import { ImageMenu } from './editor/ImageMenu';
import { Toolbar } from './editor/Toolbar';
import { CustomImage } from '../extensions/CustomImage';
import { ImageSize, ImageAlignment } from '../types/editor';
import { imageDimensions } from '../constants/editor';
import { imageStorage } from '../services/imageStorage';
import { resizeImage } from '../utils/imageUtils';
import '../styles/editor.css';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  id: string;
  onImagesChange?: (images: { id: string; file: File }[]) => void;
}

export default function RichTextEditor({ content, onChange, id, onImagesChange }: RichTextEditorProps) {
  const [showImageMenu, setShowImageMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number }>();
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      CustomImage,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'image'],
        alignments: ['left', 'center', 'right']
      }),
      TextStyle,
      FontFamily
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const handleImageUpload = useCallback(async (files: FileList, size: ImageSize, alignment: ImageAlignment) => {
    if (!editor) return;

    try {
      const currentImages = editor.getJSON()?.content?.filter(node => node.type === 'image') || [];
      const remainingSlots = 5 - currentImages.length;
      const filesToProcess = Array.from(files).slice(0, remainingSlots);

      if (filesToProcess.length === 0) {
        alert('Limite máximo de 5 imagens atingido');
        return;
      }

      for (const file of filesToProcess) {
        const resizedImage = await resizeImage(file, imageDimensions[size], imageDimensions[size]);
        const tempImage = await imageStorage.createTempImage(resizedImage);

        editor
          .chain()
          .focus()
          .insertContent({
            type: 'image',
            attrs: {
              src: tempImage.url,
              'data-id': tempImage.id,
              'data-alignment': alignment,
              width: imageDimensions[size]
            }
          })
          .run();

        if (onImagesChange) {
          const allImages = imageStorage.getAllTempImages();
          onImagesChange(allImages.map(img => ({ id: img.id, file: img.file })));
        }
      }
    } catch (error) {
      console.error('Error handling image:', error);
      alert('Erro ao processar imagem. Por favor, tente novamente.');
    }
  }, [editor, onImagesChange]);

  useEffect(() => {
    return () => {
      imageStorage.clearAllTempImages();
    };
  }, []);

  if (!editor) return null;

  return (
    <div className="border rounded-lg overflow-hidden">
      <Toolbar 
        editor={editor}
        onImageClick={(e, files) => {
          const rect = (e.target as HTMLElement).getBoundingClientRect();
          setMenuPosition({
            x: rect.left,
            y: rect.bottom + window.scrollY + 5
          });
          setSelectedFiles(files);
          setShowImageMenu(true);
        }}
        editorId={id}
      />
      
      {showImageMenu && selectedFiles && (
        <ImageMenu
          position={menuPosition}
          onInsert={async (size, alignment) => {
            await handleImageUpload(selectedFiles, size, alignment);
            setShowImageMenu(false);
            setSelectedFiles(null);
          }}
          onClose={() => {
            setShowImageMenu(false);
            setSelectedFiles(null);
          }}
        />
      )}
      
      <EditorContent 
        editor={editor} 
        className="p-4 min-h-[200px] prose dark:prose-invert max-w-none"
      />
    </div>
  );
}
